import { motion } from 'framer-motion';
import { Ruler, MousePointerClick, MessageSquare, Hammer, HardHat, CheckCircle2 } from 'lucide-react';
import Section from '../layout/Section';
import siteContent from '../../data/siteContent.json';

const iconMap = {
    measure: Ruler,
    design: MousePointerClick,
    approval: MessageSquare,
    production: Hammer,
    assembly: HardHat,
    delivery: CheckCircle2
};

const ProcessStep = ({ icon: Icon, step, title, description, isLast, index }) => (
    <div className="relative flex flex-row md:flex-col items-start md:items-center flex-1 gap-4 md:gap-0">
        {/* Connector Line */}
        {!isLast && (
            <div className="hidden md:block absolute top-8 left-1/2 w-full h-1 bg-gray-200">
                <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: "100%" }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: index * 0.5 }}
                    className="h-full bg-accent-gold"
                />
            </div>
        )}

        {/* Step Number Bubble */}
        <div className="relative z-10 w-12 h-12 md:w-16 md:h-16 rounded-full bg-white border-4 border-secondary-cream flex items-center justify-center shadow-lg mb-0 md:mb-6 group hover:border-accent-gold transition-colors duration-300 shrink-0">
            <Icon size={22} className="text-primary-charcoal group-hover:text-accent-gold transition-colors" />
            <div className="absolute -top-2 -right-2 w-5 h-5 md:w-6 md:h-6 rounded-full bg-accent-gold text-white text-[10px] md:text-xs font-bold flex items-center justify-center">
                {step}
            </div>
        </div>

        {/* Content */}
        <div className="text-left md:text-center px-0 md:px-2">
            <h3 className="font-heading font-bold text-base md:text-lg mb-1.5 md:mb-2 text-primary-charcoal">{title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed max-w-none md:max-w-[200px] md:mx-auto">
                {description}
            </p>
        </div>
    </div>
);

const Process = () => {
    const { process } = siteContent;

    return (
        <Section id="process" background="white" className="py-16 md:py-32">
            <div className="text-center max-w-3xl mx-auto mb-10 md:mb-20">
                <span className="text-primary-walnut font-bold tracking-widest uppercase text-sm mb-2 block">
                    {process.badge}
                </span>
                <h2 className="text-2xl md:text-5xl font-heading font-bold text-primary-charcoal mb-3 md:mb-4">
                    {process.title.replace("Adım Adım", "")} <br />
                    <span className="text-primary-walnut">Adım Adım</span>
                </h2>
                <p className="text-gray-600 text-base md:text-lg">
                    {process.description}
                </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
                {process.steps.map((step, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.05 }}
                        className="bg-white rounded-xl border border-secondary-grey/20 shadow-sm p-4 flex items-center gap-3"
                    >
                        <div className="relative w-12 h-12 rounded-full bg-secondary-cream flex items-center justify-center shrink-0">
                            {(() => {
                                const Icon = iconMap[step.id];
                                return Icon ? <Icon size={20} className="text-primary-charcoal" /> : null;
                            })()}
                            <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-accent-gold text-white text-[10px] font-bold flex items-center justify-center">
                                {index + 1}
                            </span>
                        </div>
                        <div>
                            <h3 className="font-heading font-semibold text-sm text-primary-charcoal mb-1">{step.title}</h3>
                            <p className="text-xs text-gray-600 leading-relaxed line-clamp-2">{step.desc}</p>
                        </div>
                    </motion.div>
                ))}
            </div>

            <div className="hidden md:flex flex-col md:flex-row gap-6 md:gap-4 relative px-0 md:px-0">
                {process.steps.map((step, index) => (
                    <ProcessStep
                        key={index}
                        step={index + 1}
                        icon={iconMap[step.id]}
                        title={step.title}
                        description={step.desc}
                        isLast={index === process.steps.length - 1}
                        index={index}
                    />
                ))}
            </div>
        </Section>
    );
};

export default Process;
